import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';

export interface CompleteUserProfile {
  // From user_profiles
  firstname: string | null;
  lastname: string | null;
  gender: "Male" | "Female" | null;
  age_range: string | null;
  country: string | null;
  town: string | null;
  postal_code: string | null;
  marketing_opt_in: boolean | null;
  avatar_url: string | null;
  
  // From community_members
  username: string | null;
  
  // From user_preferred_categories
  categoryInterests: string[];
}

export interface UseCompleteUserProfileResult {
  profile: CompleteUserProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCompleteUserProfile(): UseCompleteUserProfileResult {
  const [profile, setProfile] = useState<CompleteUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();

  const fetchProfile = async () => {
    if (!user || !currentCommunity?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user profile data
      const { data: userProfileData, error: userProfileError } = await supabase
        .from('user_profiles')
        .select('firstname, lastname, gender, age_range, country, town, postal_code, marketing_opt_in, avatar_url')
        .eq('id', user.id)
        .single();

      if (userProfileError && userProfileError.code !== 'PGRST116') {
        throw userProfileError;
      }

      // Fetch community member data (for username)
      const { data: communityMemberData, error: communityMemberError } = await supabase
        .from('community_members')
        .select('username')
        .eq('user_id', user.id)
        .eq('community_id', currentCommunity.id)
        .single();

      if (communityMemberError && communityMemberError.code !== 'PGRST116') {
        throw communityMemberError;
      }

      // Fetch user category interests
      const { data: categoryData, error: categoryError } = await supabase
        .from('user_preferred_categories')
        .select('category_id')
        .eq('user_id', user.id);

      if (categoryError) {
        throw categoryError;
      }

      // Combine all data
      const completeProfile: CompleteUserProfile = {
        firstname: userProfileData?.firstname || null,
        lastname: userProfileData?.lastname || null,
        gender: userProfileData?.gender || null,
        age_range: userProfileData?.age_range || null,
        country: userProfileData?.country || null,
        town: userProfileData?.town || null,
        postal_code: userProfileData?.postal_code || null,
        marketing_opt_in: userProfileData?.marketing_opt_in || null,
        avatar_url: userProfileData?.avatar_url || null,
        username: communityMemberData?.username || null,
        categoryInterests: categoryData?.map(c => c.category_id) || []
      };

      setProfile(completeProfile);
    } catch (err) {
      console.error('Error fetching complete user profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [user?.id, currentCommunity?.id]);

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile
  };
}